import { Suspense, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { FormikProps } from "formik"
import { useLocationState } from "../../../../../../core/router/routerHooks"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppSelector } from "../../../../../store/storeHooks"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { createUser, getUserDetails, GetUserDetailsResponse, updateUser } from "../../../../../users/userApi"
import { falsyToUndefined } from "../../../../../../lib/lang"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { enqueueSnackbar } from "notistack"
import { useLocation, useNavigate } from "react-router-dom"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { isNil } from "lodash"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { CreateOrEditTenantUserForm } from "../components/CreateOrEditTenantUserForm"
import { userType } from "../helpers/userType"
import { userTypeConfig } from "../constants/userTypeConfig"
import { CreateEditTenantUserFormValues, CreateOrUpdateUserState } from "../tenantUserTypes"
import { TenantAdminQuery } from "../../../tenantAdminApi"
import { selectEffectiveCompanyId, selectEffectiveUserGroup } from "../../../../../context/contextSelectors"

function transformUser(response: GetUserDetailsResponse) {
    return response.user
}

export function CreateOrEditTenantUser() {
    const location = useLocation()

    const navigate = useNavigate()
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userGroupId = useAppSelector(selectEffectiveUserGroup)

    const formRef = useRef<FormikProps<CreateEditTenantUserFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    const { user: userItem } = useLocationState<CreateOrUpdateUserState>({ user: undefined })
    const mode = userItem ? "update" : "create"
    const userTypeMode = userType(location.pathname)

    const { data: existingUser } = useApiQuery(getUserDetails, {
        queryName: TenantAdminQuery.DETAILS_FOR_USER,
        dependencies: {
            userId: userItem?.id ?? 0,
        },
        select: transformUser,
        enabled: Boolean(userItem),
    })

    const { mutateAsync: callCreateUser } = useApiMutation(createUser, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [userTypeConfig[userTypeMode].queryKey] })

            navigate(userTypeConfig[userTypeMode].onSuccessUrl)

            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const { mutateAsync: callUpdateUser } = useApiMutation(updateUser, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [userTypeConfig[userTypeMode].queryKey] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(TenantAdminQuery.DETAILS_FOR_USER, { userId: params.userId }),
            })

            navigate(userTypeConfig[userTypeMode].onSuccessUrl)

            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    async function handleSubmit(values: CreateEditTenantUserFormValues) {
        if (isNil(companyId) || isNil(userGroupId)) return

        if (existingUser) {
            await callUpdateUser({
                userId: existingUser.id,
                user: {
                    companyId: existingUser.companyId,
                    customerTypeId: existingUser.customerTypeId,
                    username: existingUser.username,

                    employeeId: values.employeeId,
                    displayName: values.displayName,
                    email: values.email,
                    mobileNumber: values.mobileNumber,
                    phoneNumber: values.phoneNumber,

                    settings: {
                        ignoreUserSyncJobs: values.ignoreUserSyncJobs,
                    },

                    isActive: values.isActive ?? true,
                },
            })
        } else {
            await callCreateUser({
                user: {
                    companyId,
                    customerTypeId: userGroupId,

                    employeeId: falsyToUndefined(values.employeeId),
                    displayName: values.displayName,
                    email: values.email,
                    mobileNumber: falsyToUndefined(values.mobileNumber),
                    phoneNumber: falsyToUndefined(values.phoneNumber),

                    settings: {
                        sendEmailInvitation: values.sendEmailInvitation,
                        ignoreUserSyncJobs: values.ignoreUserSyncJobs,
                    },
                },
            })
        }
    }

    return (
        <>
            <LocalizedStrict
                id={mode === "create" ? "office-manager-create-user-form-title" : "office-manager-edit-user-form-title"}
                attrs={{ title: true }}
            >
                <PageHeader
                    page={userTypeConfig[userTypeMode].page}
                    title={mode === "create" ? "Create user" : "Edit user"}
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    rightElement={
                        <LocalizedStrict
                            id={mode === "create" ? "office-manager-create-user-form" : "office-manager-edit-user-form"}
                        >
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                {mode === "create" ? "Create" : "Save"}
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="office-manager-create-user-users-breadcrumb">
                            <BreadcrumbLink to={userTypeConfig[userTypeMode].breadcrumbUrl}>Users</BreadcrumbLink>
                        </LocalizedStrict>
                        {mode === "create" ? (
                            <LocalizedStrict id="office-manager-create-user-create-breadcrumb">
                                <Breadcrumb>Create user</Breadcrumb>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="office-manager-create-user-edit-breadcrumb">
                                <Breadcrumb>Edit user</Breadcrumb>
                            </LocalizedStrict>
                        )}
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <CreateOrEditTenantUserForm ref={formRef} existingUser={existingUser} onSubmit={handleSubmit} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
